import { uniqBy } from "lodash"
import moment from "moment"
import React from "react"
import { Col, Dropdown, Form, Row } from "react-bootstrap"
import BootstrapTable, { ColumnDescription } from "react-bootstrap-table-next"
import filterFactory from "react-bootstrap-table2-filter"
import paginationFactory from "react-bootstrap-table2-paginator"
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit"
// import BootstrapTable from "react-bootstrap-table-next";
import { useTranslation } from "react-i18next"
import { SurchargeModel } from "../../api/pricing.api"
import { useAppDispatch } from "../../app/hooks"
import { SearchBar } from "../../components/Tables/Columns"
import CustomTotal from "../../components/Tables/CustomTotal"
import pageListRenderer from "../../components/Tables/PageListRenderer"
import sizePerPageOptionRenderer from "../../components/Tables/SizePerPageOptionRenderer"
import { SERVICE_TYPE } from "../../constants/constants"
import { getImageSrc } from "../../utils/commonFunctions"

function SurchargesTable({
    surcharges,
    surchargeDetails,
    setSurchargeDetails,
    setShow,
    setErrors,
    surchargeSearch,
    setSurchargeSearch
}: {
    surcharges: SurchargeModel[]
    surchargeDetails: Record<string, any>
    setSurchargeDetails: Function
    setShow: Function
    setErrors: Function
    surchargeSearch: Record<string, any>
    setSurchargeSearch: Function
}) {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    function surchargeUpdatingDate(cell: string) {
        if (cell) return moment(cell).format("DD MMM yyyy")
    }

    const handleChange = (filter: string, option: any, extra?: any) => {
        if (extra) {
            setSurchargeSearch({
                ...surchargeSearch,
                [filter]: option,
                shipping_name: extra
            })
        } else {
            setSurchargeSearch({
                ...surchargeSearch,
                [filter]: option
            })
        }
    }

    const surchargesColumns = [
        {
            text: t(""),
            dataField: "id"
        },
        {
            text: t("Surcharge Title"),
            dataField: "name"
        },
        {
            text: t("Shipping"),
            dataField: "ShippingService.shipping_service",
            formatter: (cell: string, row: Record<string, any>) => {
                return (
                    <img
                        src={getImageSrc(
                            // @ts-ignore
                            cell?.replaceAll("_", " ")?.toLowerCase()
                        )}
                    />
                )
            }
        },
        {
            text: t("Service"),
            dataField: "ShippingService.shipping_service",
            formatter: (cell: string, row: Record<string, any>) => {
                return (
                    <span className="text-capitalize">
                        {// @ts-ignore
                        cell?.replaceAll("_", " ")?.toLowerCase()}
                    </span>
                )
            }
        },
        {
            text: t("Main Surcharge"),
            dataField: "is_main",
            formatter: (cell: boolean, row: Record<string, any>) => {
                return cell ? <span className="text-success">{t("Main")}</span> : null
            }
        },
        {
            text: t("Default Value"),
            dataField: "default_value",
            formatter: (cell: number, row: Record<string, any>) => {
                return `${cell} ${row?.type === SERVICE_TYPE["0"] ? "$" : "%"}`
            },
            sort: true
        },
        {
            dataField: "",
            text: t("Actions"),
            formatter: (cell: number, row: Record<string, any>) => {
                return (
                    <Dropdown className="actions-menu">
                        <Dropdown.Toggle variant="primary" id="dropdown-basic" size={"sm"} className="py-0">
                            {t("Actions")}
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            {["Edit", "Delete"].map((action) => (
                                <Dropdown.Item
                                    className="py-0"
                                    onClick={() => {
                                        const tempSurcharge = { ...row }
                                        setSurchargeDetails(tempSurcharge)
                                        if (action === "Edit") {
                                            setShow("editOrCreateModal")
                                            setErrors({})
                                        } else {
                                            setShow("deleteConfirmModal")
                                        }
                                    }}>
                                    {action}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )
            }
        }
    ]

    const options = {
        sizePerPage: 50,
        hideSizePerPage: false,
        hidePageListOnlyOnePage: true,
        sizePerPageOptionRenderer,
        showTotal: true,
        pageListRenderer,
        paginationTotalRenderer: CustomTotal
    }

    const { ExportCSVButton } = CSVExport

    return (
        <div className="table-responsive">
            {surchargesColumns && (
                <ToolkitProvider
                    keyField="id"
                    data={surcharges}
                    columns={surchargesColumns as ColumnDescription<any, any>[]}
                    search={{ searchFormatted: true }}
                    exportCSV={{
                        fileName: "SurchargesTable.csv",
                        noAutoBOM: false,
                        exportAll: false,
                        onlyExportFiltered: true
                    }}>
                    {(props) => (
                        <div className="table-container my-4 mx-2">
                            <Row className="search shippingRates-search">
                                <Col xs={12} sm={12} md={12} lg={3}>
                                    <SearchBar {...props.searchProps} />
                                </Col>
                                <Col xs={12} sm={6} md={6} lg={2}>
                                    <Form.Select
                                        name="shipping_service_id"
                                        onChange={(e: any) => {
                                            handleChange(
                                                e.target.name,
                                                JSON.parse(e.target.value).shipping_service_id,
                                                JSON.parse(e.target.value).shipping_service
                                            )
                                        }}>
                                        <option
                                            value={JSON.stringify({
                                                shipping_service_id: "",
                                                shipping_service: ""
                                            })}>
                                            {t("All Shipping Services")}
                                        </option>
                                        {uniqBy(
                                            surcharges?.map((sr: Record<string, any>) => sr?.ShippingService),
                                            "shipping_service"
                                        )?.map((sc: any) => (
                                            <option
                                                className="text-capitalize"
                                                key={sc?.id}
                                                selected={sc?.id === surchargeSearch?.shipping_service_id}
                                                value={JSON.stringify({
                                                    shipping_service_id: sc?.id,
                                                    shipping_service: sc?.shipping_service
                                                })}>
                                                {sc?.shipping_service?.replaceAll("_", " ")?.toLowerCase()}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col xs={12} sm={6} md={6} lg={2}>
                                    <Form.Select
                                        name="shipping_company_id"
                                        onChange={(e: any) => {
                                            handleChange(
                                                e.target.name,
                                                JSON.parse(e.target.value).shipping_company_id,
                                                JSON.parse(e.target.value).shipping_name
                                            )
                                        }}>
                                        <option
                                            value={JSON.stringify({
                                                shipping_company_id: "",
                                                shipping_name: ""
                                            })}>
                                            {t("All Shipping Companies")}
                                        </option>
                                        {uniqBy(
                                            surcharges?.map(
                                                (sr: Record<string, any>) =>
                                                    sr?.ShippingService?.ShippingCompany
                                            ),
                                            "shipping_name"
                                        )?.map((sc: any) => (
                                            <option
                                                className="text-capitalize"
                                                key={sc?.id}
                                                selected={sc?.id === surchargeSearch?.shipping_company_id}
                                                value={JSON.stringify({
                                                    shipping_company_id: sc?.id,
                                                    shipping_name: sc?.shipping_name
                                                })}>
                                                {sc?.shipping_name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col className="d-none">
                                    {/* <ExportCSVButton {...props.csvProps} id="shippingRatesExportButton">
											Export
										</ExportCSVButton> */}
                                </Col>
                            </Row>
                            <Row className="table-data my-3">
                                <Col>
                                    <BootstrapTable
                                        {...props.baseProps}
                                        wrapperClasses="table-wrapper my-3 "
                                        headerClasses="header-class"
                                        bordered={false}
                                        noDataIndication={t("No_Record")}
                                        filter={filterFactory()}
                                        rowClasses="custom-table-row"
                                        condensed={true}
                                        pagination={paginationFactory(options)}
                                        sort={{
                                            dataField: "weight",
                                            order: "desc"
                                        }}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </ToolkitProvider>
            )}
        </div>
    )
}

export default SurchargesTable
